import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/auth/AuthContext";
import { useContent } from "@/content/ContentContext";
import { toast } from "sonner";
import { Save, LogOut, RotateCcw, Plus, Trash2, Eye, Loader2 } from "lucide-react";
import { Field, TextInput, TextArea, SectionCard } from "@/components/admin/Fields";
import FileUpload from "@/components/admin/FileUpload";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const { user, logout, authHeaders } = useAuth();
  const { content: liveContent, refresh } = useContent();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [registrations, setRegistrations] = useState([]);
  const [loadingRegs, setLoadingRegs] = useState(false);

  useEffect(() => {
    if (liveContent && !draft) setDraft(JSON.parse(JSON.stringify(liveContent)));
  }, [liveContent, draft]);

  const loadRegs = async () => {
    setLoadingRegs(true);
    try {
      const res = await axios.get(`${API}/admin/registrations`, {
        headers: { ...authHeaders() },
      });
      setRegistrations(res.data);
    } catch {
      toast.error("Failed to load registrations.");
    } finally {
      setLoadingRegs(false);
    }
  };

  useEffect(() => {
    loadRegs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!draft) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bone)]">
        <Loader2 className="animate-spin text-[var(--ink-soft)]" />
      </div>
    );
  }

  const setSection = (section, updater) => {
    setDraft((d) => ({ ...d, [section]: updater(d[section] || {}) }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/content`, draft, {
        headers: { ...authHeaders() },
      });
      await refresh();
      toast.success("Saved.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm("Reset all site content to the original defaults? This cannot be undone.")) return;
    try {
      const res = await axios.post(
        `${API}/admin/content/reset`,
        {},
        { headers: { ...authHeaders() } }
      );
      setDraft(JSON.parse(JSON.stringify(res.data.content)));
      await refresh();
      toast.success("Content reset to defaults.");
    } catch {
      toast.error("Reset failed.");
    }
  };

  return (
    <main data-testid="admin-page" className="min-h-screen bg-[var(--bone)] pb-32">
      {/* Top bar */}
      <header className="bg-white border-b border-[var(--line)] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <div>
            <p className="overline">Elements Of Meaford</p>
            <h1 className="font-serif-display text-xl md:text-2xl">Admin Console</h1>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="btn-secondary text-xs"
              style={{ padding: "0.6rem 1rem" }}
              data-testid="admin-view-site"
            >
              <Eye size={14} /> View site
            </a>
            <button
              onClick={resetDefaults}
              className="btn-secondary text-xs"
              style={{ padding: "0.6rem 1rem" }}
              data-testid="admin-reset-btn"
            >
              <RotateCcw size={14} /> Reset
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="btn-primary text-xs"
              style={{ padding: "0.6rem 1.2rem" }}
              data-testid="admin-save-btn"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Save changes
            </button>
            <button
              onClick={logout}
              className="text-xs uppercase tracking-[0.2em] text-[var(--ink-soft)] hover:text-[var(--ink)] flex items-center gap-2"
              data-testid="admin-logout-btn"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-10 pb-2 text-xs text-[var(--ink-soft)]">
          Signed in as {user?.email}
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-10">
        <Tabs defaultValue="hero" className="space-y-8">
          <TabsList
            className="bg-transparent flex flex-wrap gap-2 h-auto p-0"
            data-testid="admin-tabs"
          >
            {[
              ["hero", "Hero"],
              ["vision", "Vision"],
              ["configurations", "Leasing"],
              ["location", "Location"],
              ["register", "Register"],
              ["footer", "Footer"],
              ["package", "Leasing Package"],
              ["registrations", "Inquiries"],
            ].map(([v, l]) => (
              <TabsTrigger
                key={v}
                value={v}
                className="bg-white border border-[var(--line)] rounded-none px-5 py-2.5 text-xs uppercase tracking-[0.18em] font-semibold data-[state=active]:bg-[var(--ink)] data-[state=active]:text-[var(--bone)]"
                data-testid={`admin-tab-${v}`}
              >
                {l}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* HERO */}
          <TabsContent value="hero">
            <SectionCard title="Hero" description="Top-of-page banner content and image.">
              <Field label="Overline">
                <TextInput
                  value={draft.hero.overline}
                  onChange={(v) => setSection("hero", (s) => ({ ...s, overline: v }))}
                  testid="hero-overline-input"
                />
              </Field>
              <div className="grid md:grid-cols-3 gap-4">
                <Field label="Title — line 1">
                  <TextInput
                    value={draft.hero.title_line1}
                    onChange={(v) => setSection("hero", (s) => ({ ...s, title_line1: v }))}
                  />
                </Field>
                <Field label="Title — line 2 (italic)">
                  <TextInput
                    value={draft.hero.title_line2}
                    onChange={(v) => setSection("hero", (s) => ({ ...s, title_line2: v }))}
                  />
                </Field>
                <Field label="Title — line 3 (italic)">
                  <TextInput
                    value={draft.hero.title_line3}
                    onChange={(v) => setSection("hero", (s) => ({ ...s, title_line3: v }))}
                  />
                </Field>
              </div>
              <Field label="Subtitle">
                <TextArea
                  rows={3}
                  value={draft.hero.subtitle}
                  onChange={(v) => setSection("hero", (s) => ({ ...s, subtitle: v }))}
                />
              </Field>
              <Field label="Address line (bottom of hero)">
                <TextInput
                  value={draft.hero.address_line}
                  onChange={(v) => setSection("hero", (s) => ({ ...s, address_line: v }))}
                />
              </Field>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Primary CTA label">
                  <TextInput
                    value={draft.hero.cta_primary}
                    onChange={(v) => setSection("hero", (s) => ({ ...s, cta_primary: v }))}
                  />
                </Field>
                <Field label="Secondary CTA label">
                  <TextInput
                    value={draft.hero.cta_secondary}
                    onChange={(v) => setSection("hero", (s) => ({ ...s, cta_secondary: v }))}
                  />
                </Field>
              </div>
              <Field label="Hero image">
                <FileUpload
                  kind="image"
                  value={draft.hero.image_url}
                  onChange={(url) => setSection("hero", (s) => ({ ...s, image_url: url }))}
                  testid="hero-image-upload"
                  label="Upload hero image"
                />
              </Field>
            </SectionCard>
          </TabsContent>

          {/* VISION */}
          <TabsContent value="vision">
            <SectionCard title="Vision" description="The 'about' section.">
              <Field label="Overline">
                <TextInput
                  value={draft.vision.overline}
                  onChange={(v) => setSection("vision", (s) => ({ ...s, overline: v }))}
                />
              </Field>
              <Field label="Title">
                <TextArea
                  rows={2}
                  value={draft.vision.title}
                  onChange={(v) => setSection("vision", (s) => ({ ...s, title: v }))}
                />
              </Field>
              <Field label="Body paragraph 1">
                <TextArea
                  rows={3}
                  value={draft.vision.body_1}
                  onChange={(v) => setSection("vision", (s) => ({ ...s, body_1: v }))}
                />
              </Field>
              <Field label="Body paragraph 2">
                <TextArea
                  rows={3}
                  value={draft.vision.body_2}
                  onChange={(v) => setSection("vision", (s) => ({ ...s, body_2: v }))}
                />
              </Field>
              <Field label="Side image">
                <FileUpload
                  kind="image"
                  value={draft.vision.image_url}
                  onChange={(url) => setSection("vision", (s) => ({ ...s, image_url: url }))}
                />
              </Field>

              <div>
                <p className="overline mb-3">Stats</p>
                <div className="space-y-3">
                  {(draft.vision.stats || []).map((s, i) => (
                    <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_3fr_auto] gap-3 items-end border-l-2 border-[var(--terracotta)] pl-4">
                      <TextInput
                        value={s.value}
                        onChange={(v) =>
                          setSection("vision", (sec) => {
                            const list = [...(sec.stats || [])];
                            list[i] = { ...list[i], value: v };
                            return { ...sec, stats: list };
                          })
                        }
                        placeholder="12"
                      />
                      <TextInput
                        value={s.label}
                        onChange={(v) =>
                          setSection("vision", (sec) => {
                            const list = [...(sec.stats || [])];
                            list[i] = { ...list[i], label: v };
                            return { ...sec, stats: list };
                          })
                        }
                        placeholder="Collingwood St. E."
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setSection("vision", (sec) => ({
                            ...sec,
                            stats: (sec.stats || []).filter((_, idx) => idx !== i),
                          }))
                        }
                        className="text-xs text-[var(--terracotta-dark)] flex items-center gap-1"
                      >
                        <Trash2 size={14} /> Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() =>
                      setSection("vision", (sec) => ({
                        ...sec,
                        stats: [...(sec.stats || []), { value: "", label: "" }],
                      }))
                    }
                    className="btn-secondary text-xs mt-2"
                    style={{ padding: "0.5rem 1rem" }}
                  >
                    <Plus size={14} /> Add stat
                  </button>
                </div>
              </div>
            </SectionCard>
          </TabsContent>

          {/* CONFIGURATIONS */}
          <TabsContent value="configurations">
            <SectionCard
              title="Leasing Configurations"
              description="Residential + commercial showcase. Manage units and upload floor-plan layouts."
            >
              <Field label="Overline">
                <TextInput
                  value={draft.configurations.overline}
                  onChange={(v) => setSection("configurations", (s) => ({ ...s, overline: v }))}
                />
              </Field>
              <Field label="Title">
                <TextArea
                  rows={2}
                  value={draft.configurations.title}
                  onChange={(v) => setSection("configurations", (s) => ({ ...s, title: v }))}
                />
              </Field>
              <Field label="Intro paragraph">
                <TextArea
                  rows={3}
                  value={draft.configurations.intro}
                  onChange={(v) => setSection("configurations", (s) => ({ ...s, intro: v }))}
                />
              </Field>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4 border border-[var(--line)] p-4">
                  <p className="overline">Residential feature</p>
                  <TextInput
                    value={draft.configurations.residential_title}
                    onChange={(v) =>
                      setSection("configurations", (s) => ({ ...s, residential_title: v }))
                    }
                    placeholder="Headline"
                  />
                  <TextArea
                    rows={3}
                    value={draft.configurations.residential_body}
                    onChange={(v) =>
                      setSection("configurations", (s) => ({ ...s, residential_body: v }))
                    }
                  />
                  <FileUpload
                    kind="image"
                    value={draft.configurations.residential_image_url}
                    onChange={(url) =>
                      setSection("configurations", (s) => ({
                        ...s,
                        residential_image_url: url,
                      }))
                    }
                  />
                </div>
                <div className="space-y-4 border border-[var(--line)] p-4">
                  <p className="overline">Commercial feature</p>
                  <TextInput
                    value={draft.configurations.commercial_title}
                    onChange={(v) =>
                      setSection("configurations", (s) => ({ ...s, commercial_title: v }))
                    }
                  />
                  <TextArea
                    rows={3}
                    value={draft.configurations.commercial_body}
                    onChange={(v) =>
                      setSection("configurations", (s) => ({ ...s, commercial_body: v }))
                    }
                  />
                  <FileUpload
                    kind="image"
                    value={draft.configurations.commercial_image_url}
                    onChange={(url) =>
                      setSection("configurations", (s) => ({
                        ...s,
                        commercial_image_url: url,
                      }))
                    }
                  />
                </div>
              </div>

              <div>
                <p className="overline mb-3">Configuration units</p>
                <div className="space-y-4">
                  {(draft.configurations.units || []).map((u, i) => (
                    <div key={i} className="border border-[var(--line)] p-4 space-y-3">
                      <div className="grid md:grid-cols-[80px_1fr_140px] gap-3">
                        <TextInput
                          value={u.code}
                          onChange={(v) =>
                            setSection("configurations", (s) => {
                              const list = [...(s.units || [])];
                              list[i] = { ...list[i], code: v };
                              return { ...s, units: list };
                            })
                          }
                          placeholder="01"
                        />
                        <TextInput
                          value={u.title}
                          onChange={(v) =>
                            setSection("configurations", (s) => {
                              const list = [...(s.units || [])];
                              list[i] = { ...list[i], title: v };
                              return { ...s, units: list };
                            })
                          }
                          placeholder="Title"
                        />
                        <TextInput
                          value={u.tag}
                          onChange={(v) =>
                            setSection("configurations", (s) => {
                              const list = [...(s.units || [])];
                              list[i] = { ...list[i], tag: v };
                              return { ...s, units: list };
                            })
                          }
                          placeholder="Residential"
                        />
                      </div>
                      <TextArea
                        rows={2}
                        value={u.desc}
                        onChange={(v) =>
                          setSection("configurations", (s) => {
                            const list = [...(s.units || [])];
                            list[i] = { ...list[i], desc: v };
                            return { ...s, units: list };
                          })
                        }
                      />
                      <Field label="Layout (PDF or image)">
                        <FileUpload
                          kind="layout"
                          value={u.layout_url}
                          onChange={(url, info) =>
                            setSection("configurations", (s) => {
                              const list = [...(s.units || [])];
                              list[i] = {
                                ...list[i],
                                layout_url: url,
                                layout_filename: info?.filename || "",
                              };
                              return { ...s, units: list };
                            })
                          }
                          testid={`unit-${u.code}-layout-upload`}
                        />
                      </Field>
                      <button
                        type="button"
                        onClick={() =>
                          setSection("configurations", (s) => ({
                            ...s,
                            units: (s.units || []).filter((_, idx) => idx !== i),
                          }))
                        }
                        className="text-xs text-[var(--terracotta-dark)] flex items-center gap-1"
                      >
                        <Trash2 size={14} /> Remove unit
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() =>
                      setSection("configurations", (s) => ({
                        ...s,
                        units: [
                          ...(s.units || []),
                          { code: "", title: "", desc: "", tag: "Residential", layout_url: "" },
                        ],
                      }))
                    }
                    className="btn-secondary text-xs"
                    style={{ padding: "0.5rem 1rem" }}
                  >
                    <Plus size={14} /> Add unit
                  </button>
                </div>
              </div>
            </SectionCard>
          </TabsContent>

          {/* LOCATION */}
          <TabsContent value="location">
            <SectionCard title="Location" description="Map, address, and neighbourhood list.">
              <Field label="Overline">
                <TextInput
                  value={draft.location.overline}
                  onChange={(v) => setSection("location", (s) => ({ ...s, overline: v }))}
                />
              </Field>
              <Field label="Title">
                <TextArea
                  rows={2}
                  value={draft.location.title}
                  onChange={(v) => setSection("location", (s) => ({ ...s, title: v }))}
                />
              </Field>
              <Field label="Body">
                <TextArea
                  rows={3}
                  value={draft.location.body}
                  onChange={(v) => setSection("location", (s) => ({ ...s, body: v }))}
                />
              </Field>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Address line 1">
                  <TextInput
                    value={draft.location.address_line_1}
                    onChange={(v) => setSection("location", (s) => ({ ...s, address_line_1: v }))}
                  />
                </Field>
                <Field label="Address line 2">
                  <TextInput
                    value={draft.location.address_line_2}
                    onChange={(v) => setSection("location", (s) => ({ ...s, address_line_2: v }))}
                  />
                </Field>
                <Field label="Phone">
                  <TextInput
                    value={draft.location.phone}
                    onChange={(v) => setSection("location", (s) => ({ ...s, phone: v }))}
                  />
                </Field>
                <Field label="Email">
                  <TextInput
                    value={draft.location.email}
                    onChange={(v) => setSection("location", (s) => ({ ...s, email: v }))}
                  />
                </Field>
                <Field label="Google Maps query">
                  <TextInput
                    value={draft.location.map_query}
                    onChange={(v) => setSection("location", (s) => ({ ...s, map_query: v }))}
                    placeholder="12 Collingwood Street East, Meaford, ON"
                  />
                </Field>
              </div>

              <div>
                <p className="overline mb-3">In the neighbourhood</p>
                <div className="space-y-3">
                  {(draft.location.nearby || []).map((n, i) => (
                    <div
                      key={i}
                      className="grid md:grid-cols-[2fr_1fr_auto] gap-3 items-end border-l-2 border-[var(--terracotta)] pl-4"
                    >
                      <TextInput
                        value={n.name}
                        onChange={(v) =>
                          setSection("location", (s) => {
                            const list = [...(s.nearby || [])];
                            list[i] = { ...list[i], name: v };
                            return { ...s, nearby: list };
                          })
                        }
                        placeholder="Place"
                      />
                      <TextInput
                        value={n.distance}
                        onChange={(v) =>
                          setSection("location", (s) => {
                            const list = [...(s.nearby || [])];
                            list[i] = { ...list[i], distance: v };
                            return { ...s, nearby: list };
                          })
                        }
                        placeholder="3 min walk"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setSection("location", (s) => ({
                            ...s,
                            nearby: (s.nearby || []).filter((_, idx) => idx !== i),
                          }))
                        }
                        className="text-xs text-[var(--terracotta-dark)] flex items-center gap-1"
                      >
                        <Trash2 size={14} /> Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() =>
                      setSection("location", (s) => ({
                        ...s,
                        nearby: [...(s.nearby || []), { name: "", distance: "" }],
                      }))
                    }
                    className="btn-secondary text-xs"
                    style={{ padding: "0.5rem 1rem" }}
                  >
                    <Plus size={14} /> Add place
                  </button>
                </div>
              </div>
            </SectionCard>
          </TabsContent>

          {/* REGISTER */}
          <TabsContent value="register">
            <SectionCard title="Register" description="Copy shown next to the registration form.">
              <Field label="Overline">
                <TextInput
                  value={draft.register.overline}
                  onChange={(v) => setSection("register", (s) => ({ ...s, overline: v }))}
                />
              </Field>
              <Field label="Title">
                <TextArea
                  rows={2}
                  value={draft.register.title}
                  onChange={(v) => setSection("register", (s) => ({ ...s, title: v }))}
                />
              </Field>
              <Field label="Body">
                <TextArea
                  rows={3}
                  value={draft.register.body}
                  onChange={(v) => setSection("register", (s) => ({ ...s, body: v }))}
                />
              </Field>
              <Field label="Disclaimer (under submit button)">
                <TextArea
                  rows={2}
                  value={draft.register.disclaimer}
                  onChange={(v) => setSection("register", (s) => ({ ...s, disclaimer: v }))}
                />
              </Field>
            </SectionCard>
          </TabsContent>

          {/* FOOTER */}
          <TabsContent value="footer">
            <SectionCard title="Footer" description="Bottom-of-page contact block.">
              <Field label="Tagline">
                <TextArea
                  rows={3}
                  value={draft.footer.tagline}
                  onChange={(v) => setSection("footer", (s) => ({ ...s, tagline: v }))}
                />
              </Field>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Address line 1">
                  <TextInput
                    value={draft.footer.address_line_1}
                    onChange={(v) => setSection("footer", (s) => ({ ...s, address_line_1: v }))}
                  />
                </Field>
                <Field label="Address line 2">
                  <TextInput
                    value={draft.footer.address_line_2}
                    onChange={(v) => setSection("footer", (s) => ({ ...s, address_line_2: v }))}
                  />
                </Field>
                <Field label="Phone">
                  <TextInput
                    value={draft.footer.phone}
                    onChange={(v) => setSection("footer", (s) => ({ ...s, phone: v }))}
                  />
                </Field>
                <Field label="Email">
                  <TextInput
                    value={draft.footer.email}
                    onChange={(v) => setSection("footer", (s) => ({ ...s, email: v }))}
                  />
                </Field>
              </div>
            </SectionCard>
          </TabsContent>

          {/* LEASING PACKAGE */}
          <TabsContent value="package">
            <SectionCard
              title="Leasing Package"
              description="Single downloadable PDF. Auto-emailed to anyone who registers and surfaced as a 'Download Leasing Package' button on the public site."
            >
              <Field label="Leasing package file (PDF preferred)">
                <FileUpload
                  kind="package"
                  value={draft.leasing_package?.url}
                  onChange={(url, info) =>
                    setDraft((d) => ({
                      ...d,
                      leasing_package: {
                        url,
                        filename: info?.filename || "",
                        uploaded_at: new Date().toISOString(),
                      },
                    }))
                  }
                  testid="leasing-package-upload"
                  label="Upload leasing package"
                />
              </Field>
              {draft.leasing_package?.filename && (
                <p className="text-xs text-[var(--ink-soft)]">
                  Current file: <strong>{draft.leasing_package.filename}</strong>
                  {draft.leasing_package.uploaded_at &&
                    ` · uploaded ${new Date(draft.leasing_package.uploaded_at).toLocaleString()}`}
                </p>
              )}
            </SectionCard>
          </TabsContent>

          {/* REGISTRATIONS */}
          <TabsContent value="registrations">
            <SectionCard
              title="Registrations / Inquiries"
              description={`Latest leads (${registrations.length}). Notification emails also sent to ${liveContent?.footer?.email || "hello@elementsofmeaford.com"}.`}
            >
              <div className="flex items-center justify-between">
                <button
                  onClick={loadRegs}
                  className="btn-secondary text-xs"
                  style={{ padding: "0.5rem 1rem" }}
                  data-testid="refresh-registrations"
                >
                  {loadingRegs ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
                  Refresh
                </button>
              </div>
              <div className="overflow-x-auto -mx-2">
                <table className="w-full text-sm" data-testid="registrations-table">
                  <thead className="text-left">
                    <tr className="border-b border-[var(--line)]">
                      <th className="overline py-3 px-2">Received</th>
                      <th className="overline py-3 px-2">Name</th>
                      <th className="overline py-3 px-2">Company</th>
                      <th className="overline py-3 px-2">Phone</th>
                      <th className="overline py-3 px-2">Email</th>
                      <th className="overline py-3 px-2">City</th>
                    </tr>
                  </thead>
                  <tbody>
                    {registrations.length === 0 && (
                      <tr>
                        <td colSpan={6} className="py-10 text-center text-[var(--ink-soft)]">
                          No registrations yet.
                        </td>
                      </tr>
                    )}
                    {registrations.map((r) => (
                      <tr key={r.id} className="border-b border-[var(--line)] hover:bg-[var(--bone-2)]/40">
                        <td className="py-3 px-2 text-xs text-[var(--ink-soft)] whitespace-nowrap">
                          {new Date(r.created_at).toLocaleString()}
                        </td>
                        <td className="py-3 px-2 font-medium">{r.name}</td>
                        <td className="py-3 px-2">{r.company_name || "—"}</td>
                        <td className="py-3 px-2 whitespace-nowrap">{r.phone}</td>
                        <td className="py-3 px-2">{r.email}</td>
                        <td className="py-3 px-2">{r.current_city}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
}
